import { DesignSystemProvider, Notifications } from '@bynder/design-system';
import type { Editor } from '@tiptap/react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ErrorBoundary } from './components/error-boundary.tsx';
import { FormatsToolbar } from './components/formats-toolbar/formats-toolbar.tsx';
import { GlobalStyles } from './components/global-styles.tsx';
import { ReadOnlyEditor } from './components/read-only-editor.tsx';
import { TeaEditor } from './components/tea-editor/tea-editor.tsx';
import { teaErrorNotifier } from './components/tea-error-notifier.ts';
import { TeaNetwork } from './components/tea-network.tsx';
import { TeaContextProvider } from './contexts/tea-context.tsx';
import type { EditorFormats } from './data/.types/editorFormats.ts';
import type { EditorOptions } from './data/.types/editorOptions.ts';
import type { DocumentData } from './data/.types/tea.ts';
import { TeaStorageClient } from './data/document/api/TeaStorageClient.ts';
import type {
  CreateDocumentBody,
  MetaProperty,
} from './data/document/api/createDocument';
import { TeaTracking } from './tea-tracking.ts';

type InflightDocumentRequestType = ReturnType<
  TeaStorageClient['createDocument'] | TeaStorageClient['loadDocument']
>;

export type TeaProps = {
  documentUuid: string;
  documentGroupId?: string;
  meta?: MetaProperty;
  accessToken: string;
  onError?: (error: Error) => void;
  readOnly?: boolean;
  baseUrl?: string;
  removeDefaultFocusStyle?: boolean;
  editorFormats?: EditorFormats;
  editorOptions?: EditorOptions;
  environment?: string;
  children?: ReactNode;
  document?: DocumentData['document'];
  html?: DocumentData['html'];
};

const createInitialDocumentState = ({
  document,
  html,
  text,
}: Partial<DocumentData>) => {
  const data = {} as DocumentData;
  if (document) {
    data.document = document;
  }
  if (html) {
    data.html = html;
  }
  if (text) {
    data.text = text;
  }

  if (Object.keys(data).length === 0) {
    return null;
  }

  return data;
};

function Tea({
  documentUuid,
  documentGroupId,
  meta = {},
  accessToken,
  onError = teaErrorNotifier,
  baseUrl = '/api/1/authoring',
  readOnly = false,
  removeDefaultFocusStyle = false,
  editorFormats = {},
  environment = 'development',
  children,
  document,
  html,
}: TeaProps) {
  const tracking = useMemo(
    () => new TeaTracking(baseUrl, accessToken, meta, documentUuid),
    [baseUrl, accessToken, meta, documentUuid],
  );
  const [editor, setEditor] = useState<Editor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [documentData, setDocumentData] = useState<null | DocumentData>(
    createInitialDocumentState({ document, html }),
  );
  const inflightDocumentRequest = useRef<InflightDocumentRequestType>(null);

  const teaStorage = useMemo(
    () =>
      new TeaStorageClient(
        accessToken,
        baseUrl,
        documentGroupId,
        documentUuid,
        window.location.toString(),
        meta,
      ),
    [accessToken, baseUrl, documentGroupId, documentUuid, meta],
  );

  const loadDocument = useCallback(() => {
    setIsLoading(true);

    // Track current requests to prevent duplicates
    inflightDocumentRequest.current = teaStorage.loadDocument();
    inflightDocumentRequest.current
      .then(setDocumentData)
      .catch((error) => {
        // If 404, try creating a new document
        // NOTE: This workflow is up for discussion and likely to change in the future
        if (error.message.includes('404')) {
          inflightDocumentRequest.current = teaStorage.createDocument();
          inflightDocumentRequest.current.then(setDocumentData).catch(onError);
        } else {
          if (onError) {
            onError(error);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [teaStorage.createDocument, teaStorage.loadDocument, onError]);

  const handleSaveDocument = async (body: CreateDocumentBody) => {
    try {
      return await teaStorage.saveDocument(body);
    } catch (err) {
      onError(err as Error);
    }
  };

  useEffect(() => {
    setDocumentData(createInitialDocumentState({ document, html }));
  }, [document, html]);

  useEffect(() => {
    if (documentData === null && inflightDocumentRequest.current === null)
      loadDocument();
  }, [documentData, loadDocument]);

  const teaContextValue = useMemo(
    () => ({
      editor,
      setEditor,
      editorFormats,
      baseUrl,
    }),
    [editor, editorFormats, baseUrl],
  );

  return (
    <DesignSystemProvider>
      <TeaContextProvider value={teaContextValue}>
        <ErrorBoundary environment={environment}>
          <Notifications />
          <div className="tea-wrapper">
            <GlobalStyles />
            {documentData && readOnly ? (
              <Tea.ReadOnlyEditor
                html={documentData.html}
                documentUuid={documentUuid}
              />
            ) : null}
            {isLoading ? <div data-testid="loading">Loading...</div> : null}
            {documentData && !readOnly ? (
              <>
                {children}
                <TeaEditor
                  onError={onError}
                  documentUuid={documentUuid}
                  documentGroupId={documentGroupId}
                  documentData={documentData}
                  saveDocument={handleSaveDocument}
                  meta={meta}
                  editorFormats={editorFormats}
                  removeDefaultFocusStyle={removeDefaultFocusStyle}
                  recordEvent={tracking.recordEvent}
                />
              </>
            ) : null}
          </div>
        </ErrorBoundary>
      </TeaContextProvider>
    </DesignSystemProvider>
  );
}

Tea.ReadOnlyEditor = ReadOnlyEditor;
Tea.Toolbar = FormatsToolbar;
Tea.Network = TeaNetwork;

export { Tea };
